/**
 * Risk Scorecard Component
 *
 * Wiz-style visual scorecard showing pass rate, severity breakdown, and compliance status.
 * Consolidates Executive Summary into a compact, actionable visual format.
 */

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, AlertTriangle, Shield, ShieldAlert } from "lucide-react";
import type { LLMScanSummary, LLMScanResult, ScanVerdict } from "@/types/llm";
import { calculateFrameworkCoverage } from "@/utils/complianceFrameworks";

interface RiskScorecardProps {
  summary: LLMScanSummary;
  results?: LLMScanResult[];
  isLoading?: boolean;
  isPartialData?: boolean;
  verdict?: ScanVerdict;
}

type RiskLevel = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

function getRiskLevel(severityBreakdown: {
  critical: number;
  high: number;
  medium: number;
  low: number;
}): RiskLevel {
  if (severityBreakdown.critical > 0) return "CRITICAL";
  if (severityBreakdown.high > 0) return "HIGH";
  if (severityBreakdown.medium > 0) return "MEDIUM";
  return "LOW";
}

function getRiskConfig(riskLevel: RiskLevel) {
  switch (riskLevel) {
    case "CRITICAL":
      return {
        color: "text-red-600",
        bg: "bg-red-50",
        border: "border-red-200",
        icon: ShieldAlert,
      };
    case "HIGH":
      return {
        color: "text-orange-600",
        bg: "bg-orange-50",
        border: "border-orange-200",
        icon: AlertTriangle,
      };
    case "MEDIUM":
      return {
        color: "text-yellow-600",
        bg: "bg-yellow-50",
        border: "border-yellow-200",
        icon: AlertTriangle,
      };
    case "LOW":
      return {
        color: "text-green-600",
        bg: "bg-green-50",
        border: "border-green-200",
        icon: CheckCircle2,
      };
  }
}

export const RiskScorecard: React.FC<RiskScorecardProps> = ({
  summary,
  results = [],
  isLoading = false,
  isPartialData = false,
  verdict,
}) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const riskLevel = getRiskLevel(summary.severity_breakdown);
  const riskConfig = getRiskConfig(riskLevel);

  // Outcome-driven framing (decision-led): FAIL => red, PASS => green.
  const isPass = verdict?.verdict === "PASS";
  const hasBlocking = (verdict?.blockingCount ?? 0) > 0;
  const outcomeBorderClass = verdict
    ? isPass
      ? "border-green-200"
      : hasBlocking
        ? "border-red-200"
        : "border-yellow-200"
    : riskConfig.border;

  // Extract frameworks from config
  const frameworks = summary.scan.config_content?.redteam?.frameworks || [];
  const frameworkCoverage =
    frameworks.length > 0 && results.length > 0
      ? calculateFrameworkCoverage(frameworks, results)
      : [];

  const severityItems = [
    {
      label: "Critical",
      count: summary.severity_breakdown.critical,
      color: "bg-red-500",
      textColor: "text-red-600",
    },
    {
      label: "High",
      count: summary.severity_breakdown.high,
      color: "bg-orange-500",
      textColor: "text-orange-600",
    },
    {
      label: "Medium",
      count: summary.severity_breakdown.medium,
      color: "bg-yellow-500",
      textColor: "text-yellow-600",
    },
    {
      label: "Low",
      count: summary.severity_breakdown.low,
      color: "bg-gray-400",
      textColor: "text-gray-600",
    },
  ];

  const hasIssues = summary.failed_findings > 0;
  const maxCount = Math.max(...severityItems.map((s) => s.count), 1);

  return (
    <Card className={`${outcomeBorderClass} border-2`}>
      <CardContent className="p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Verdict Section */}
          <div className="flex flex-col">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Review Outcome
            </div>
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                  isPass
                    ? "bg-green-100 border-green-300"
                    : hasBlocking
                      ? "bg-red-100 border-red-300"
                      : "bg-yellow-100 border-yellow-300"
                }`}
              >
                {isPass ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : hasBlocking ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-lg font-bold text-gray-900">
                  {isPass ? "Passed" : "Failed"}
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  {isPass
                    ? "This system is ready for security or compliance review."
                    : "This system would not be approved in a standard security or compliance review."}
                </p>
              </div>
            </div>
          </div>

          {/* Severity Breakdown Section */}
          <div className="flex flex-col">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Severity Breakdown
            </div>
            {hasIssues ? (
              <div className="space-y-2">
                {severityItems.map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className="w-12 text-xs font-medium text-right">{item.label}</div>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div
                        className={`h-full ${item.color} transition-all duration-300`}
                        style={{ width: `${(item.count / maxCount) * 100}%` }}
                      />
                    </div>
                    <div className={`w-6 text-xs font-bold ${item.textColor}`}>{item.count}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-3 py-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-600">All Clear</p>
                  <p className="text-xs text-muted-foreground">No security issues found</p>
                </div>
              </div>
            )}
          </div>

          {/* Compliance Coverage Section */}
          <div className="flex flex-col">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Compliance Coverage
            </div>
            {frameworkCoverage.length > 0 ? (
              <div className="space-y-2">
                {frameworkCoverage.slice(0, 3).map((fw) => (
                  <div key={fw.name} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium truncate">{fw.name}</span>
                      <span className="text-muted-foreground">{fw.coverage}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          fw.coverage === 100
                            ? "bg-green-500"
                            : fw.coverage >= 80
                              ? "bg-blue-500"
                              : "bg-yellow-500"
                        }`}
                        style={{ width: `${fw.coverage}%` }}
                      />
                    </div>
                  </div>
                ))}
                {frameworkCoverage.length > 3 && (
                  <p className="text-xs text-muted-foreground">
                    +{frameworkCoverage.length - 3} more frameworks
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 py-4">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Not Configured</p>
                  <p className="text-xs text-muted-foreground">Add frameworks in scan config</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Risk Level Badge */}
        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <div className="flex items-center gap-2">
            <riskConfig.icon className={`h-4 w-4 ${riskConfig.color}`} />
            <span className="text-xs text-muted-foreground">Overall Risk Level:</span>
            <Badge className={`${riskConfig.bg} ${riskConfig.color} border-0 text-xs`}>
              {riskLevel}
            </Badge>
            {isPartialData && (
              <Badge variant="outline" className="text-xs">
                Partial data
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {summary.scan.total_tests} tests • {summary.failed_findings} failed
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
